import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Lazy-loaded pages
const CatalogueList = React.lazy(() => import("./CatalogueList"));
const SourcesList = React.lazy(() => import("./SourcesList"));
const VideoViewer = React.lazy(() => import("./VideoViewer"));
const ResearchBases = React.lazy(() => import("./ResearchBases"));

/**
 * Transcript Miner Module Config
 *
 * SINGLE SOURCE OF TRUTH: permissions are defined ONCE in the backend
 * (transcript-miner/permissions.ts) and resolved at runtime via the
 * modulePermissions query. The empty arrays below are placeholders.
 */
const transcriptMinerModuleConfig: ModuleConfig = {
  id: "transcript-miner",
  name: "Transcript Miner",
  description: "Point at a YouTube channel → archive every video (1080p) to IPFS + auto-transcribe with speaker diarization. Watch mode keeps pulling new uploads.",
  version: "2.0.0",
  enabled: true,
  category: "tools",
  order: 21,
  icon: "⛏️", // ⛏️
  color: "#8B5CF6",

  routes: [
    { path: "/transcript-miner", component: CatalogueList, permissionLevel: "view", useLayout: true },
    { path: "/transcript-miner/sources", component: SourcesList, permissionLevel: "view", useLayout: true },
    { path: "/transcript-miner/bases", component: ResearchBases, permissionLevel: "view", useLayout: true },
    { path: "/transcript-miner/:id", component: VideoViewer, permissionLevel: "view", useLayout: true },
  ],

  navigation: [
    { label: "Catalogue", path: "/transcript-miner", icon: "🎬", permissionLevel: "view" },
    { label: "Channels & Sources", path: "/transcript-miner/sources", icon: "📡", permissionLevel: "view" },
    { label: "Research Bases", path: "/transcript-miner/bases", icon: "🧠", permissionLevel: "view" },
  ],

  permissions: { view: [], create: [], edit: [], delete: [] },

  quickActions: [
    { label: "Add a Channel", path: "/transcript-miner/sources", icon: "➕", description: "Point at a YouTube channel or playlist to harvest" },
    { label: "Browse Catalogue", path: "/transcript-miner", icon: "🎬", description: "Watch archived videos + read transcripts" },
    { label: "Research Bases", path: "/transcript-miner/bases", icon: "🧠", description: "Turn transcripts into a browsable wiki + rollups" },
  ],

  changelog: [
    {
      version: "2.0.0",
      releaseDate: "2026-08-08",
      title: "Research bases, wiki pages and rollups",
      features: [
        "Point a channel at a RESEARCH BASE — a machine, a biz_in_a_folder_* root and a subfolder (create the folder inline)",
        "Every transcribed clip becomes a wiki page with its thumbnail, speakers, and claims that deep-link to the second they were said",
        "Full diarized transcripts written as markdown the Knowledge Explorer can read, search and chat over",
        "Rollups: the base proposes the next logical scope, runs a map-reduce synthesis, and writes it with per-claim evidence",
        "Duplicate-name review that writes verdicts into taxonomy/aliases.md, so a merge sticks across re-runs",
        "Wayback snapshots of every source page (the page — the video's permanence is the IPFS pin)",
        "Deep links carry the machine as well as the folder, so a base link can be shared",
      ],
      notes:
        "The folder is written through one seam with two backends (local disk / knowledge-fs over HTTP) and a content-hash manifest: " +
        "unchanged files are skipped, and files a previous build wrote are reported as stale rather than deleted — knowledge-fs has no delete endpoint.",
    },
    {
      version: "1.0.0",
      releaseDate: "2026-07-19",
      title: "Initial Release — Transcript Miner",
      features: [
        "Point at a YouTube channel / playlist / video",
        "Full 1080p video archived permanently to IPFS + in-app player",
        "Speaker-diarized transcripts via the Mac Studio Whisper server",
        "Two decoupled queues — catalogue plays before transcripts finish",
        "Watch mode: auto-pull new uploads on a cadence",
      ],
      notes: "Phase 1: catalogue + archive + transcript. Placeholders for BitChute/Rumble/feeds and public-IPFS publishing.",
    },
  ],

  roadmap: [
    {
      title: "Knowledge graph over transcripts",
      description: "Transcript .md knowledge base + Knowledge Explorer graph mode + rollups. Shipped 2026-08-08.",
      targetVersion: "2.0.0",
      priority: "medium",
      status: "completed",
      features: ["Transcript .md knowledge base", "Wikilink colour graph", "Rollups with evidenced claims", "Duplicate-name review"],
    },
    {
      title: "Web-enriched rollups",
      description: "Let a rollup pull web sources via the harness's Brave tools — rendered in their own 🌐 section, never blended with transcript claims.",
      targetVersion: "2.1.0",
      priority: "medium",
      status: "planned",
      features: ["Per-person life timelines", "Web sources with Wayback snapshots", "Provenance-labelled synthesis"],
    },
    {
      title: "More platforms + feeds",
      description: "BitChute / Rumble / Vimeo adapters + RSS/podcast feed ingestion.",
      targetVersion: "1.1.0",
      priority: "low",
      status: "planned",
      features: ["BitChute", "Rumble", "Vimeo", "RSS/Atom feeds"],
    },
  ],
};

export default transcriptMinerModuleConfig;
